import type {
  ComponentDetailsData,
  ComponentDetailsSection,
  DefectDetailsData,
  DefectDetailsSection,
  JobDetailsData,
  JobDetailsSection,
  RepairKitsData,
  RepairKitsSection,
  RepairOptionsData,
  RepairOptionsSection,
} from "@stryker/core/types/compositeSections";
import type { ExpandStatus } from "@stryker/core/types/expandable";
import type { ExpandableCards } from "@stryker/core/types/jobs";
import type {
  AdditionalJobDetailsData,
  AdditionalJobDetailsSection,
  DesignConditionsData,
  DesignConditionsSection,
} from "@stryker/core/types/sections";
import InfoIcon from "@/assets/icons/info.svg?react";
import { Typography } from "@/components/ui";

import {
  AdditionalForcesAndMoments,
  AdditionalJobDetails,
  BuriedDetails,
  Checks,
  CompliantCheck,
  ComponentDetails,
  ContactInformation,
  DefectDetails,
  DesignConditions,
  DiameterEncompassingEntireThinDefectDetails,
  ExternalPressureVacuum,
  ExtraLayers,
  JobDetails,
  Notes,
  OperatingConditions,
  PressureCycles,
  SlotPinholeDetails,
  UTReadingDetails,
} from "../components";
import { RepairKits } from "../components/RepairKits";
import { RepairOptions } from "../components/RepairOptions";

interface JobFormSectionsProps {
  temperatureUnitsOptions: Array<{ title: string; value: string }>;
  sections: {
    jobDetails: JobDetailsSection;
    additionalJobDetails: AdditionalJobDetailsSection;
    designConditions: DesignConditionsSection;
    componentDetails: ComponentDetailsSection;
    defectDetails: DefectDetailsSection;
    repairOptions: RepairOptionsSection;
    repairKits: RepairKitsSection;
  };
  setJobDetails: (payload: { data: JobDetailsData; status: ExpandStatus }) => void;
  setAdditionalJobDetails: (payload: { data?: AdditionalJobDetailsData; status: ExpandStatus }) => void;
  toggleExpandableSection: (section: ExpandableCards) => void;
  setDesignConditions: (payload: { data: DesignConditionsData; status: ExpandStatus }) => void;
  setComponentDetails: (payload: { data: ComponentDetailsData; status: ExpandStatus }) => void;
  setDefectDetails: (payload: { data: DefectDetailsData; status: ExpandStatus }) => void;
  setRepairOptions: (payload: { data: RepairOptionsData; status: ExpandStatus }) => void;
  setRepairKits: (payload: { data: RepairKitsData; status: ExpandStatus }) => void;
}

export function JobFormSections({
  temperatureUnitsOptions,
  sections,
  setJobDetails,
  setAdditionalJobDetails,
  toggleExpandableSection,
  setDesignConditions,
  setComponentDetails,
  setDefectDetails,
  setRepairOptions,
  setRepairKits,
}: JobFormSectionsProps) {
  return (
    <>
      <div className="my-9 border-gray-200 border-b border-solid pb-5">
        <Typography variant="text-lg" weight="semibold">
          Main information
        </Typography>
        <div className="flex items-center gap-1">
          <InfoIcon />
          <Typography variant="text-small">Complete each stage of the form</Typography>
        </div>
      </div>
      <div className="flex flex-col gap-4 rounded bg-gray-150 py-6 md:px-6">
        <JobDetails
          setJobDetails={setJobDetails}
          savedJobDetails={sections.jobDetails}
          toggleExpandableSection={toggleExpandableSection}
        />
        <AdditionalJobDetails
          setAdditionalJobDetails={setAdditionalJobDetails}
          savedAdditionalJobDetails={sections.additionalJobDetails}
          toggleExpandableSection={toggleExpandableSection}
        />
        <ContactInformation />
        <DesignConditions
          temperatureUnitsOptions={temperatureUnitsOptions}
          jobDetails={sections.jobDetails}
          savedDesignConditions={sections.designConditions}
          setDesignConditions={setDesignConditions}
          toggleExpandableSection={toggleExpandableSection}
        />
        <OperatingConditions temperatureUnitsOptions={temperatureUnitsOptions} />
        <ComponentDetails
          toggleExpandableSection={toggleExpandableSection}
          savedComponentDetails={sections.componentDetails}
          setComponentDetails={setComponentDetails}
          designCode={sections.jobDetails.data.design_code}
        />
        <DefectDetails
          savedDefectDetails={sections.defectDetails}
          toggleExpandableSection={toggleExpandableSection}
          componentDetailsData={sections.componentDetails.data}
          setDefectDetails={setDefectDetails}
        />
        <RepairOptions
          savedRepairOptions={sections.repairOptions}
          toggleExpandableSection={toggleExpandableSection}
          jobDetailsData={sections.jobDetails.data}
          setRepairOptions={setRepairOptions}
        />
        <RepairKits
          savedRepairKits={sections.repairKits}
          toggleExpandableSection={toggleExpandableSection}
          jobDetailsData={sections.jobDetails.data}
          setRepairKits={setRepairKits}
        />
        <CompliantCheck />
        <Checks />
        <SlotPinholeDetails />
        <DiameterEncompassingEntireThinDefectDetails />
        <PressureCycles />
        <AdditionalForcesAndMoments />
        <ExternalPressureVacuum />
        <UTReadingDetails />
        <BuriedDetails />
        <ExtraLayers />
        <Notes />
      </div>
    </>
  );
}
